import React from "react";
import {
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  Divider,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Icon,
  Label,
  Stack,
  StackItem
} from "@patternfly/react-core";

import { Icons } from "shared/components/Icons";
import { LOGO_SIZES } from "shared/constants/logoSizes";
import { erasmusLogos } from "../config/logos";
import useAppearance from "shared/appearance/hooks/useAppearance";
import { erasmusLogosService } from "../services/erasmusLogosService";
import ErasmusNewsCard from "./ErasmusNewsCard";
import { useLatestErasmusPost } from "../hooks/useLatestErasmusPost";

const ErasmusInfoCard = ({
  icon,
  label,
  title,
  children
}) => (
  <Card isFullHeight isGlass>
    <CardTitle>
      <Stack hasGutter>
        <StackItem>
          <Bullseye
            style={{
              width: "6rem",
              height: "6rem",
              borderRadius:
                "var(--pf-t--global--border--radius--medium)",
              backgroundColor:
                "var(--pf-t--global--background--color--secondary--default)",
              border:
                "var(--pf-t--global--border--width--regular) solid var(--pf-t--global--border--color--default)"
            }}
          >
            <Icon size="2xl">
              {icon}
            </Icon>
          </Bullseye>
        </StackItem>

        <StackItem>
          <Label color="blue">
            {label}
          </Label>
        </StackItem>

        <StackItem>
          <Content component="h2">
            {title}
          </Content>
        </StackItem>
      </Stack>
    </CardTitle>

    <CardBody>
      <Content component="p">
        {children}
      </Content>
    </CardBody>
  </Card>
);

const ErasmusPanel = () => {
  const { colorScheme } = useAppearance();

  const logos = erasmusLogosService.getLogos({
    colorScheme,
  });

  const {
    post,
    isLoading
  } = useLatestErasmusPost();
  return (
    <Stack hasGutter>
      <StackItem>
        <Flex
          justifyContent={{ default: "justifyContentCenter" }}
          alignItems={{ default: "alignItemsCenter" }}
          spaceItems={{ default: "spaceItemsMd" }}
        >
          {logos.map((logo) => (
            <FlexItem key={logo.id}>
              <img
                src={logo.src}
                alt={logo.alt}
                loading="eager"
                style={{
                  height: `${
                    logo.height ??
                    LOGO_SIZES.desktop[logo.size]
                  }px`,
                  width: "auto",
                  display: "block",
                  ...(logo.offset && {
                    transform: `translate(
                      ${logo.offset.x}px,
                      ${logo.offset.y}px
                    )`,
                  }),
                }}
              />
            </FlexItem>
          ))}
        </Flex>
      </StackItem>

      <StackItem>
        <Divider />
      </StackItem>

      <StackItem>
        <Grid>
          <GridItem
            span={10}
            offset={1}
            lg={8}
            lgOffset={2}
          >
            <Stack hasGutter>
              <StackItem>
                <Content
                  component="h1"
                  style={{ textAlign: "center" }}
                >
                  Erasmus+ Praktyki Zawodowe
                </Content>
              </StackItem>

              <StackItem>
                <Content
                  component="p"
                  style={{ textAlign: "center" }}
                >
                  W naszej szkole realizowany jest program Erasmus+.
                  Umożliwia on uczniom techników i szkół branżowych
                  odbycie praktyk zawodowych w zagranicznych firmach
                  na terenie Unii Europejskiej. To wyjątkowa okazja
                  do zdobycia doświadczenia zawodowego, rozwijania
                  kompetencji językowych oraz poznawania nowych kultur
                  i technologii pracy.
                </Content>
              </StackItem>
            </Stack>
          </GridItem>
        </Grid>
      </StackItem>

      <StackItem>
        <Grid hasGutter>
          <GridItem span={12} sm={6} lg={3}>
            <ErasmusNewsCard
              post={post}
              isLoading={isLoading}
            />
          </GridItem>

          <GridItem span={12} sm={6} lg={3}>
            <ErasmusInfoCard
              icon={Icons.ErasmusDuration}
              label="14 dni"
              title="Typ mobilności"
            >
              Program obejmuje krótkoterminowe praktyki zawodowe
              trwające minimum 14 dni, połączone z przygotowaniem
              językowym i kulturowym.
            </ErasmusInfoCard>
          </GridItem>

          <GridItem span={12} sm={6} lg={3}>
            <ErasmusInfoCard
              icon={Icons.ErasmusFunding}
              label="Finansowanie"
              title="Dofinansowanie"
            >
              Grant Erasmus+ pokrywa koszty podróży,
              zakwaterowania, wyżywienia oraz organizacji pobytu
              uczestników za granicą.
            </ErasmusInfoCard>
          </GridItem>

          <GridItem span={12} sm={6} lg={3}>
            <ErasmusInfoCard
              icon={Icons.ErasmusBenefits}
              label="Korzyści"
              title="Co zyskujesz?"
            >
              Uczestnicy rozwijają kompetencje zawodowe i językowe,
              zdobywają praktyczne doświadczenie oraz otrzymują
              certyfikat Europass-Mobilność.
            </ErasmusInfoCard>
          </GridItem>
        </Grid>
      </StackItem>

      <StackItem>
        <Flex justifyContent={{ default: "justifyContentCenter" }}>
          <FlexItem>
            <Button
              variant="primary"
              component="a"
              href="/projects/erasmus"
            >
              Czytaj więcej
            </Button>
          </FlexItem>
          <FlexItem>
            <Button
              variant="secondary"
              component="a"
              href="https://erasmusplus.org.pl/sektory/ksztalcenie-i-szkolenia-zawodowe/akcja-1-mobilnosc-edukacyjna"
              target="_blank"
              rel="noopener noreferrer"
            >
              Strona główna Erasmus+
            </Button>
          </FlexItem>
        </Flex>
      </StackItem>
    </Stack>
  );
};

export default ErasmusPanel;